"use client";

import { ArrowRight, Heart } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HealthAwarenessHero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const credentials = ["Community Driven", "Impact Focused", "Awareness First", "Ethical Merch"];
  return <section data-section-id="238" id="healthawarenesshero" className="bg-background text-foreground relative overflow-hidden">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "radial-gradient(var(--foreground) 1px, transparent 1px)",
      backgroundSize: "3px 3px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-12 sm:px-8 lg:px-12 lg:pt-28 lg:pb-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <span className="inline-block h-2 w-2 rounded-lg" style={{
              backgroundColor: "var(--chart-1)"
            }} aria-hidden="true" />
              <span>Forever Fresh</span>
              <span className="text-muted-foreground/50">/</span>
              <span>Humanitarian Awareness</span>
            </div>

            {}
            <h1 className="font-default mt-8 text-5xl font-bold leading-[1.02] text-foreground sm:text-6xl lg:text-7xl xl:text-8xl" style={{
            letterSpacing: "-0.03em"
          }}>
              Keeping issues{" "}
              <span className="relative inline-block italic">
                <span style={{
                color: "var(--chart-1)"
              }}>forever fresh</span>
                <svg className="absolute -bottom-2 left-0 h-3 w-full" viewBox="0 0 300 12" preserveAspectRatio="none" fill="none" aria-hidden="true">
                  <path d="M2 8 Q 75 2, 150 6 T 298 5" stroke="var(--chart-3)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                </svg>
              </span>
              .
            </h1>

            {}
            <p className="font-default mt-8 max-w-[540px] text-lg leading-[1.65] text-muted-foreground">
              We bridge the gap between advocacy and action. By combining education 
              with limited-edition apparel, we ensure vital awareness issues remain 
              at the forefront of the conversation while funding real-world change.
            </p>

            {}
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Button asChild size="lg" className="h-12 rounded-lg px-7 text-sm font-medium shadow-md hover:shadow-lg transition-shadow bg-primary text-primary-foreground">
                <Link to="/CurrentCausesPage">
                  Explore Causes
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>

              <Link to="/ShopPage" className="group inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors">
                <span className="border-b border-foreground/40 pb-0.5 group-hover:border-primary">
                  Shop Current Drop
                </span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {}
            <Separator className="mt-12 bg-border/60" />

            {}
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              {credentials.map((cred, i) => <div key={cred} data-index={i} className="flex items-center gap-4">
                  {i > 0 && <span className="text-muted-foreground/40">·</span>}
                  <span>{cred}</span>
                </div>)}
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative">
              {}
              <div className="relative overflow-hidden rounded-lg shadow-lg" style={{
              aspectRatio: "4/5"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_awareness_event.webp" alt="Community members gathering for a humanitarian awareness event" loading="lazy" decoding="async" className="h-full w-full object-cover" style={{
                filter: "saturate(0.95) brightness(0.98)"
              }} />
                {}
                <div className="absolute inset-0 mix-blend-overlay opacity-20" style={{
                background: "linear-gradient(135deg, var(--chart-1) 0%, transparent 50%, var(--chart-3) 100%)"
              }} aria-hidden="true" />
              </div>

              {}
              <div className="absolute -bottom-6 -left-4 sm:left-6 max-w-[260px] rounded-lg border border-border/40 bg-card/70 p-5 shadow-md backdrop-blur-md">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      Impact Raised
                    </div>
                    <div className="font-default mt-1.5 text-2xl font-bold text-foreground" style={{
                    letterSpacing: "-0.02em"
                  }}>
                      $150k+
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      donated to our partners
                    </div>
                  </div>
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{
                  backgroundColor: "var(--chart-1)",
                  color: "var(--primary-foreground)"
                }}>
                    <Heart className="h-4 w-4" />
                  </div>
                </div>

                {}
                <svg className="mt-3 w-full" viewBox="0 0 200 36" preserveAspectRatio="none" fill="none" aria-hidden="true">
                  <path d="M0 28 L 25 24 L 50 26 L 75 20 L 100 18 L 125 14 L 150 12 L 175 6 L 200 4" stroke="var(--chart-1)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <path d="M0 28 L 25 24 L 50 26 L 75 20 L 100 18 L 125 14 L 150 12 L 175 6 L 200 4 L 200 36 L 0 36 Z" fill="var(--chart-1)" fillOpacity="0.12" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {}
        <Separator className="mt-24 bg-border/40" />
      </div>
    </section>;
}